import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Alert,
  Button,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  getReinvestmentPreview,
  submitReinvestment,
  getReinvestmentCreditValidation,
  getScreenConstraint,
  openScreenAccess,
  restrictScreenAccess,
} from "../../../../api/investmentLeague";
import {
  REINVESTMENT_MOCK_PREVIEW,
  INVESTMENT_LEAGUE_ADMIN_EMAILS,
  isInvestmentLeagueAdmin,
  formatCurrency,
} from "../../../../constants/investmentLeague";
import { mapReinvestmentPreviewResponse } from "../../utils/mapApiData";
import ReinvestHeader from "./ReinvestHeader";
import ReinvestSummaryCards from "./ReinvestSummaryCards";
import JackpotWarningBanner from "./JackpotWarningBanner";
import ReinvestTable from "./ReinvestTable";
import ReinvestSidebar from "./ReinvestSidebar";
import PreviousInvestmentsModal from "./PreviousInvestmentsModal";
import ScreenAccessOverlay from "./ScreenAccessOverlay";
import SectionLoader from "../SectionLoader";

const ReinvestQ3 = ({ isAdmin: propIsAdmin, currentUser }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Determine whether the current user is an Investment League Admin using INVESTMENT_LEAGUE_ADMIN_EMAILS
  const userEmail = currentUser?.email || "";
  const isAdmin = useMemo(() => {
    if (propIsAdmin !== undefined) {
      return Boolean(propIsAdmin);
    }
    if (userEmail) {
      return (
        INVESTMENT_LEAGUE_ADMIN_EMAILS.includes(userEmail.toLowerCase()) ||
        isInvestmentLeagueAdmin(userEmail)
      );
    }
    return false;
  }, [propIsAdmin, userEmail]);

  // Screen Access Control State
  const [openAccess, setOpenAccess] = useState(false);
  const [accessChecking, setAccessChecking] = useState(true);
  const [isOpeningAccess, setIsOpeningAccess] = useState(false);
  const [isRestrictingAccess, setIsRestrictingAccess] = useState(false);
  const [overlayError, setOverlayError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Authoritative server-side credit validation response
  const [validationData, setValidationData] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [postSubmitValidationError, setPostSubmitValidationError] = useState(false);

  // Allocations state: { [unitKey]: number }
  const [allocations, setAllocations] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notifications
  const [successMessage, setSuccessMessage] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [toastError, setToastError] = useState("");

  // Modal for Previous Investments
  const [showPreviousModal, setShowPreviousModal] = useState(false);

  // Initial Data Fetch: Preview + Credit Validation
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setValidationError(null);
    setPostSubmitValidationError(false);

    try {
      const [previewRes, valRes] = await Promise.allSettled([
        getReinvestmentPreview(),
        getReinvestmentCreditValidation("Q3"),
      ]);

      // 1. Process Preview Response
      if (previewRes.status === "fulfilled") {
        const raw = previewRes.value?.data || previewRes.value || REINVESTMENT_MOCK_PREVIEW;
        const previewData = mapReinvestmentPreviewResponse(raw);
        setData(previewData);

        const initialMap = {};
        (previewData.allocations || []).forEach((u) => {
          initialMap[u.buUnit] = u.currentQuarterInvestment || 0;
        });
        setAllocations(initialMap);
      } else {
        console.warn("Reinvestment preview API unavailable, using fallback:", previewRes.reason);
        const previewData = mapReinvestmentPreviewResponse(REINVESTMENT_MOCK_PREVIEW);
        setData(previewData);
        const initialMap = {};
        previewData.allocations.forEach((u) => {
          initialMap[u.buUnit] = u.currentQuarterInvestment || 0;
        });
        setAllocations(initialMap);
      }

      // 2. Process Credit Validation Response
      if (valRes.status === "fulfilled" && valRes.value) {
        const valData = valRes.value?.data || valRes.value;
        setValidationData(valData);
        setIsDirty(false);
      } else {
        console.error("Credit validation API failed on load:", valRes.reason);
        setValidationError("Unable to validate your remaining credit. Please refresh and try again.");
      }
    } catch (err) {
      console.error("Failed to load initial reinvestment data:", err);
      setError("Failed to load reinvestment details from server.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial check: Screen constraint + Reinvestment data
  useEffect(() => {
    let isMounted = true;

    const checkAccessAndLoad = async () => {
      setAccessChecking(true);
      setOverlayError(null);

      try {
        // FIRST operation: GET /investmentleague/screen-constraint?screenName=Re-invest%20for%20Q3
        const constraintRes = await getScreenConstraint("Re-invest for Q3");
        if (isMounted) {
          const isOpen = Boolean(constraintRes?.openAccess);
          setOpenAccess(isOpen);
        }
      } catch (err) {
        console.error("Screen constraint check failed on load:", err);
        if (isMounted) {
          // Safer behavior: If the access state cannot be retrieved, keep the page locked
          setOpenAccess(false);
          setOverlayError("Unable to retrieve screen access status. Screen remains locked.");
        }
      } finally {
        if (isMounted) {
          setAccessChecking(false);
        }
      }

      if (isMounted) {
        loadInitialData();
      }
    };

    checkAccessAndLoad();

    return () => {
      isMounted = false;
    };
  }, [loadInitialData]);

  // Open Access Handler for Admins
  const handleOpenAccess = async () => {
    setIsOpeningAccess(true);
    setOverlayError(null);

    try {
      // Step 1: POST /investmentleague/screen-constraint
      await openScreenAccess("Re-invest for Q3", true);

      // Step 2: GET /investmentleague/screen-constraint?screenName=Re-invest%20for%20Q3 (source of truth)
      const verifyRes = await getScreenConstraint("Re-invest for Q3");
      const isNowOpen = Boolean(verifyRes?.openAccess);

      if (isNowOpen) {
        setOpenAccess(true);
        setOverlayError(null);
        setSuccessMessage("Re-invest for Q3 is now open.");
      } else {
        // If GET still returns false, keep page locked
        setOpenAccess(false);
        setOverlayError("Access has not been opened yet. Please try again.");
      }
    } catch (err) {
      console.error("Failed to open screen access:", err);
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 403
          ? "Only Investment League Admins can open this screen."
          : "Unable to update screen access.");
      setOverlayError(msg);
      setOpenAccess(false);
    } finally {
      setIsOpeningAccess(false);
    }
  };

  // Restrict Access Handler for Admins (closes/locks the screen again)
  const handleRestrictAccess = async () => {
    setIsRestrictingAccess(true);

    try {
      // Step 1: POST /investmentleague/screen-constraint { screenName, openAccess: false }
      await restrictScreenAccess("Re-invest for Q3");

      // Step 2: GET /investmentleague/screen-constraint to verify source of truth
      const verifyRes = await getScreenConstraint("Re-invest for Q3");
      const isNowOpen = Boolean(verifyRes?.openAccess);

      if (!isNowOpen) {
        setOpenAccess(false);
        setOverlayError(null);
        setSuccessMessage("Screen access is now restricted.");
      } else {
        const errorMsg = "Access restriction could not be confirmed. Please try again.";
        setSubmissionError(errorMsg);
        setToastError(errorMsg);
      }
    } catch (err) {
      console.error("Failed to restrict screen access:", err);
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 403
          ? "Only Investment League Admins can restrict this screen."
          : "Unable to restrict screen access.");
      setSubmissionError(msg);
      setToastError(msg);
    } finally {
      setIsRestrictingAccess(false);
    }
  };

  // Retry credit validation manually if initial call failed
  const handleRetryValidation = async () => {
    setValidationError(null);
    setPostSubmitValidationError(false);
    try {
      const upcoming = validationData?.upcomingQuarter || data?.upcomingQuarter || "Q3";
      const res = await getReinvestmentCreditValidation(upcoming);
      const valData = res?.data || res;
      setValidationData(valData);
      setIsDirty(false);
    } catch (err) {
      console.error("Retry credit validation failed:", err);
      setValidationError("Unable to validate your remaining credit. Please refresh and try again.");
    }
  };

  // Handler for individual allocation input changes
  const handleAllocationChange = (unitKey, value) => {
    setAllocations((prev) => ({
      ...prev,
      [unitKey]: value,
    }));
    setIsDirty(true);
    if (submissionError) setSubmissionError("");
  };

  // Calculations & memoized values (placed before any early returns)
  const previousQuarterCredit = validationData?.previousQuarterCredit != null
    ? Number(validationData.previousQuarterCredit)
    : (data?.availableCredit || 0);

  const availableCredit = previousQuarterCredit;
  const investorUnit = validationData?.investorUnit || data?.investorUnit || (currentUser?.macCode ? `MAC ${currentUser.macCode}` : "MAC 2 - Hercules");
  const previousQuarter = validationData?.previousQuarter || data?.previousQuarter || "Q2";
  const upcomingQuarter = validationData?.upcomingQuarter || data?.upcomingQuarter || "Q3";

  const units = useMemo(() => data?.allocations || data?.units || [], [data]);

  const windowInfo = useMemo(() => {
    return data?.window || data?.reinvestmentWindow || {
      startDate: "01 Sep 2026",
      endDate: "31 Oct 2026",
      startDateFormatted: "01 Sep 2026",
      endDateFormatted: "31 Oct 2026",
      displayDates: "01 Sep 2026 – 31 Oct 2026",
      isOpen: true,
    };
  }, [data]);

  const jackpotInfo = useMemo(() => {
    return data?.jackpot || data?.jackpotEligibility || {};
  }, [data]);

  // Clean investor unit name for self-exclusion
  const cleanInvestorUnit = useMemo(() => {
    return (investorUnit || "").trim().toLowerCase();
  }, [investorUnit]);

  const recipientUnits = useMemo(() => {
    return units.filter((unit) => {
      const uName = (unit.buUnit || unit.unitName || "").toLowerCase();
      const uCode = (unit.unitCode || "").toLowerCase();
      if (!cleanInvestorUnit) return true;
      return !cleanInvestorUnit.includes(uCode) && !cleanInvestorUnit.includes(uName);
    });
  }, [units, cleanInvestorUnit]);

  // Local live calculation during active typing
  const localAllocatedAmount = useMemo(() => {
    return Object.entries(allocations).reduce((sum, [unitKey, amt]) => {
      const isRecipient = recipientUnits.some(
        (u) => (u.buUnit || u.unitName || u.unitCode) === unitKey
      );
      if (isRecipient && typeof amt === "number" && amt > 0) {
        return sum + amt;
      }
      return sum;
    }, 0);
  }, [allocations, recipientUnits]);

  const localRemainingCredit = availableCredit - localAllocatedAmount;
  const localPercentage = availableCredit > 0
    ? (localAllocatedAmount / availableCredit) * 100
    : 0;

  // Determine whether to display server-confirmed balance or local estimated balance
  const isServerConfirmed = !isDirty && validationData != null;

  const displayedAllocatedAmount = isServerConfirmed
    ? Number(validationData.allocatedAmount)
    : localAllocatedAmount;

  const displayedRemainingCredit = isServerConfirmed
    ? Number(validationData.remainingCredit)
    : localRemainingCredit;

  const displayedPercentage = isServerConfirmed && validationData.allocationPercentage != null
    ? Number(validationData.allocationPercentage)
    : localPercentage;

  const isOverAllocated = isServerConfirmed
    ? Boolean(validationData.isOverAllocated || displayedRemainingCredit < 0)
    : localRemainingCredit < 0;

  const isFullyAllocated = isServerConfirmed
    ? Boolean(validationData.isFullyAllocated || (displayedRemainingCredit === 0 && displayedAllocatedAmount > 0))
    : (localRemainingCredit === 0 && localAllocatedAmount > 0);

  // Jackpot Continuity Checks
  const requiredUnits = useMemo(() => {
    const reqList = jackpotInfo?.requiredPreviousUnits;
    if (Array.isArray(reqList) && reqList.length > 0) {
      return recipientUnits.filter((u) => reqList.includes(u.buUnit) || u.requiredForJackpot);
    }
    return recipientUnits.filter(
      (u) =>
        u.requiredForJackpot ||
        u.investedInPreviousQuarter ||
        u.isRequiredForJackpot ||
        (u.previousQuarterInvestment > 0) ||
        (u.q1Invested > 0 || u.q2Invested > 0)
    );
  }, [recipientUnits, jackpotInfo?.requiredPreviousUnits]);

  const missingUnits = useMemo(() => {
    return requiredUnits.filter((u) => {
      const key = u.buUnit || u.unitName || u.unitCode;
      const amt = allocations[key];
      return amt === undefined || amt === null || amt === 0 || amt === "";
    });
  }, [requiredUnits, allocations]);

  // Handle Form Submission
  const handleSubmit = async () => {
    if (!openAccess) return;

    if (isOverAllocated) {
      setSubmissionError(`Your ${upcomingQuarter} allocation exceeds your available ${previousQuarter} credit.`);
      return;
    }

    if (!windowInfo.isOpen) {
      setSubmissionError(`${upcomingQuarter} reinvestment is currently closed.`);
      return;
    }

    if (localAllocatedAmount === 0 && (!isServerConfirmed || displayedAllocatedAmount === 0)) {
      setSubmissionError("Please allocate an amount before submitting.");
      return;
    }

    setIsSubmitting(true);
    setSubmissionError("");
    setPostSubmitValidationError(false);

    // Build payload: exclude InvestorUnit, only include buUnit and amount
    const payloadAllocations = Object.entries(allocations)
      .filter(([unitKey, amt]) => {
        const isRecipient = recipientUnits.some(
          (u) => (u.buUnit || u.unitName || u.unitCode) === unitKey
        );
        return isRecipient && amt > 0;
      })
      .map(([buUnit, amount]) => ({
        buUnit,
        amount,
      }));

    const payload = {
      quarter: upcomingQuarter,
      allocations: payloadAllocations,
    };

    try {
      await submitReinvestment(payload);

      // Step 1 & 2: Refresh credit validation from server
      try {
        const valRes = await getReinvestmentCreditValidation(upcomingQuarter);
        const valResult = valRes?.data || valRes;
        if (valResult) {
          setValidationData(valResult);
          setIsDirty(false); // Server-confirmed values replace local calculated values
        }
      } catch (valErr) {
        console.error("Credit validation refresh failed after submit:", valErr);
        setPostSubmitValidationError(true);
      }

      // Step 3: Refresh the reinvestment preview API as well (to update jackpot status & allocations)
      try {
        const prevRes = await getReinvestmentPreview();
        const raw = prevRes?.data || prevRes || REINVESTMENT_MOCK_PREVIEW;
        const previewData = mapReinvestmentPreviewResponse(raw);
        setData(previewData);

        // Update allocations map to reflect persisted server state
        const refreshedMap = {};
        (previewData.allocations || []).forEach((u) => {
          refreshedMap[u.buUnit] = u.currentQuarterInvestment || 0;
        });
        setAllocations(refreshedMap);
      } catch (prevErr) {
        console.warn("Reinvestment preview refresh failed after submit:", prevErr);
      }

      setSuccessMessage(`${upcomingQuarter} investments submitted successfully.`);
    } catch (err) {
      console.error("Failed to submit reinvestment:", err);
      const message =
        err?.response?.data?.message ||
        `Failed to submit ${upcomingQuarter} investments. Please try again.`;
      setSubmissionError(message);
      setToastError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Early returns placed safely AFTER all hooks
  if (accessChecking) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <SectionLoader message="Checking screen access..." />
      </Box>
    );
  }

  if (loading && !data && !validationData && openAccess) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <SectionLoader message={`Loading ${upcomingQuarter} Re-Investment details...`} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: !openAccess ? "calc(100vh - 120px)" : "auto",
        maxHeight: !openAccess ? "calc(100vh - 120px)" : "none",
        overflow: !openAccess ? "hidden" : "visible",
        pb: 4,
      }}
    >
      {/* Protected Content Container */}
      <Box
        sx={{
          filter: !openAccess ? "blur(6px)" : "none",
          pointerEvents: !openAccess ? "none" : "auto",
          userSelect: !openAccess ? "none" : "auto",
          opacity: !openAccess ? 0.6 : 1,
          transition: "filter 0.3s ease, opacity 0.3s ease",
        }}
        aria-hidden={!openAccess}
        tabIndex={!openAccess ? -1 : undefined}
      >
      {/* 1. Page Header */}
      <ReinvestHeader
        windowInfo={windowInfo}
        previousQuarter={previousQuarter}
        upcomingQuarter={upcomingQuarter}
        isAdmin={isAdmin}
        onRestrictAccess={handleRestrictAccess}
        isRestricting={isRestrictingAccess}
      />

      {/* General Error Alert */}
      {error && (
        <Alert
          severity="warning"
          sx={{
            backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.1)",
            color: isDark ? "#FDE68A" : "#92400E",
            border: isDark ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(245, 158, 11, 0.25)",
            mb: 2.5,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Validation API Error Alert with Retry */}
      {validationError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetryValidation}
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Retry
            </Button>
          }
          sx={{
            backgroundColor: isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.1)",
            color: isDark ? "#FCA5A5" : "#B91C1C",
            border: isDark ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.25)",
            mb: 2.5,
          }}
        >
          {validationError}
        </Alert>
      )}

      {/* Post-Submit Validation API Error Alert with Refresh */}
      {postSubmitValidationError && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetryValidation}
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Refresh
            </Button>
          }
          sx={{
            backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.1)",
            color: isDark ? "#FDE68A" : "#92400E",
            border: isDark ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(245, 158, 11, 0.25)",
            mb: 2.5,
          }}
        >
          Your {upcomingQuarter} investment was submitted, but we could not refresh your remaining credit. Please refresh the page to verify the latest balance.
        </Alert>
      )}

      {/* Fully Allocated Info Alert */}
      {isFullyAllocated && !isOverAllocated && (
        <Alert
          severity="info"
          sx={{
            backgroundColor: isDark ? "rgba(56, 189, 248, 0.15)" : "rgba(40, 179, 180, 0.12)",
            color: isDark ? "#7DD3FC" : "#03514D",
            border: isDark ? "1px solid rgba(56, 189, 248, 0.3)" : "1px solid rgba(40, 179, 180, 0.25)",
            mb: 2.5,
          }}
        >
          Your full {previousQuarter} credit has been allocated to {upcomingQuarter}. Remaining: $0
        </Alert>
      )}

      {/* Over-allocation Error Alert */}
      {isOverAllocated && (
        <Alert
          severity="error"
          sx={{
            backgroundColor: isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.1)",
            color: isDark ? "#FCA5A5" : "#B91C1C",
            border: isDark ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.25)",
            mb: 2.5,
          }}
        >
          <Box sx={{ fontWeight: 700, mb: 0.5 }}>
            Your {upcomingQuarter} allocation exceeds your available {previousQuarter} credit.
          </Box>
          <Box sx={{ fontSize: "0.82rem", opacity: 0.9 }}>
            Available: {formatCurrency(availableCredit)} &nbsp;|&nbsp; Allocated: {formatCurrency(displayedAllocatedAmount)} &nbsp;|&nbsp; Remaining: $0
          </Box>
        </Alert>
      )}

      {/* Submission Error Alert */}
      {submissionError && (
        <Alert
          severity="error"
          sx={{
            backgroundColor: isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.1)",
            color: isDark ? "#FCA5A5" : "#B91C1C",
            border: isDark ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.25)",
            mb: 2.5,
          }}
        >
          {submissionError}
        </Alert>
      )}

      {/* 2. Summary Cards (4 KPIs) */}
      <ReinvestSummaryCards
        availableCredit={availableCredit}
        allocatedAmount={displayedAllocatedAmount}
        remainingCredit={displayedRemainingCredit}
        allocationPercentage={displayedPercentage}
        isServerConfirmed={isServerConfirmed}
        isFullyAllocated={isFullyAllocated}
        isOverAllocated={isOverAllocated}
        investorUnit={investorUnit}
        eligibleUnitsCount={recipientUnits.length}
        previousQuarter={previousQuarter}
        upcomingQuarter={upcomingQuarter}
      />

      {/* 3. Jackpot Warning Banner */}
      <JackpotWarningBanner
        requiredCount={requiredUnits.length}
        missingUnits={missingUnits}
        onViewPreviousInvestments={() => setShowPreviousModal(true)}
        previousQuarter={previousQuarter}
        upcomingQuarter={upcomingQuarter}
      />

      {/* 4. Main Two-Column Layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 340px", xl: "1fr 360px" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        {/* Left Column: Allocation Table */}
        <Box sx={{ minWidth: 0 }}>
          <ReinvestTable
            units={units}
            allocations={allocations}
            onAllocationChange={handleAllocationChange}
            investorUnit={investorUnit}
            disabled={!windowInfo.isOpen || isSubmitting}
            previousQuarter={previousQuarter}
            upcomingQuarter={upcomingQuarter}
          />
        </Box>

        {/* Right Column: Sidebar */}
        <Box sx={{ minWidth: 0 }}>
          <ReinvestSidebar
            availableCredit={availableCredit}
            allocatedAmount={displayedAllocatedAmount}
            remainingCredit={displayedRemainingCredit}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            windowIsOpen={windowInfo.isOpen}
            windowEndDate={windowInfo.endDateFormatted || windowInfo.endDate}
            investorUnit={investorUnit}
            jackpotEligibility={jackpotInfo}
            missingUnits={missingUnits}
            previousQuarter={previousQuarter}
            upcomingQuarter={upcomingQuarter}
            isFullyAllocated={isFullyAllocated}
            isOverAllocated={isOverAllocated}
          />
        </Box>
      </Box>
      </Box>

      {/* Locked Screen Overlay */}
      {!openAccess && (
        <ScreenAccessOverlay
          isAdmin={isAdmin}
          onOpenAccess={handleOpenAccess}
          isOpening={isOpeningAccess}
          errorMessage={overlayError}
          screenTitle={`Re-Invest for ${upcomingQuarter}`}
        />
      )}

      {/* Previous Investments Breakdown Modal */}
      <PreviousInvestmentsModal
        open={showPreviousModal}
        onClose={() => setShowPreviousModal(false)}
        units={units}
        investorUnit={investorUnit}
        previousQuarter={previousQuarter}
      />

      {/* Success Notification Snackbar (Bottom Right) */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
        }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            backgroundColor: isDark ? "#064e3b" : "#ECFDF5",
            color: isDark ? "#a7f3d0" : "#065F46",
            border: isDark ? "1px solid #10b981" : "1px solid #10b981",
            fontWeight: 600,
            boxShadow: isDark ? "0 4px 16px rgba(0, 0, 0, 0.4)" : "0 4px 16px rgba(3, 81, 77, 0.15)",
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Notification Snackbar (Bottom Right) */}
      <Snackbar
        open={Boolean(toastError)}
        autoHideDuration={6000}
        onClose={() => setToastError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
        }}
      >
        <Alert
          onClose={() => setToastError("")}
          severity="error"
          sx={{
            backgroundColor: isDark ? "#7f1d1d" : "#FEF2F2",
            color: isDark ? "#fecaca" : "#991B1B",
            border: isDark ? "1px solid #ef4444" : "1px solid #f87171",
            fontWeight: 600,
            boxShadow: isDark ? "0 4px 16px rgba(0, 0, 0, 0.4)" : "0 4px 16px rgba(220, 38, 38, 0.15)",
          }}
        >
          {toastError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReinvestQ3;
